import { useForm } from 'react-hook-form';
import { dietrydata, dietrySchema } from '../schema/dietary-report.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Clock3, Utensils } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/components/form/form';
import { Input } from 'src/components/input/input';
import { Button } from 'src/components/buttons/shadcn-button';
import { useAddDietReport } from '../hooks/useAddDietReport';

const DietaryReportForm = ({customerId, memberId}: {customerId: number, memberId: number}) => {

  const form = useForm<dietrydata>({
    resolver: zodResolver(dietrySchema),
  });

  const { mutate: addDietReport, isPending } = useAddDietReport();

  const onSubmit = async (data: dietrydata) => {
    if (!customerId && !memberId) {
      toast.error("Select Member First");
    }

    addDietReport({data, customerId, memberId});
  };

  if (!memberId || !customerId) {
    return (
      <div className="flex justify-center items-center bg-amber-50 rounded-xl p-5 max-w-2xl mx-auto my-4">
        <h3 className="text-amber-600 text-semibold">
          Select Member to Add Dietary Report.
        </h3>
      </div>
    );
  }
  
  return (
    <div className="max-w-5xl mx-auto flex justify-center py-8">
      <div className=" bg-card shadow-lg rounded-2xl w-full p-4 border border-gray-100">
        <h2 className="font-medium text-[#42679e] text-xl mb-6 border-b pb-3 flex gap-2 items-center justify-center">
          <Utensils size={20} className="" /> <span>Dietary Recall Parameters</span> 
        </h2>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Day */}
            <div className="flex gap-6 items-center rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
              <span className="font-medium min-w-[150px] text-center border-b p-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-xl text-[#42679e]">
                Day
              </span>
              <FormField
                control={form.control}
                name="day"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <select
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="border border-gray-300 rounded-xl px-3 py-2 text-[#42679e] font-medium focus:border-blue-500"
                      >
                        <option value="">Select day</option>
                        {["5", "4", "3", "2", "1"].map((d) => (
                          <option value={d} key={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className='grid grid-cols-2 gap-3 w-full'>
              {[
                {
                  title: "Morning Breakfast",
                  timeName: "morning_meal_fast_time",
                  mealName: "morning_meal_fast",
                },
                {
                  title: "Lunch",
                  timeName: "lunch_time",
                  mealName: "lunch",
                },
                {
                  title: "Evening snacks",
                  timeName: "evening_snacks_time",
                  mealName: "evening_snacks",
                },
                {
                  title: "Dinner",
                  timeName: "dinner_time",
                  mealName: "dinner",
                },
                {
                  title: "Anything In Between",
                  timeName: "anything_between_time",
                  mealName: "anything_between",
                },
              ].map((section, i) => (
                <div
                  key={i}
                  className="rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition"
                >
                  <h3 className="font-medium mb-3 border-b pb-1 bg-gradient-to-r from-blue-100 to-blue-50 rounded-xl p-2 text-[#42679e]">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-2 gap-5 items-center">
                    {/* Time field with clock icon */}
                    <div className="relative">
                      <Clock3 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <FormField
                        control={form.control}
                        name={section.timeName as keyof dietrydata}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="time"
                                className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-[#42679e] font-medium"
                                {...field}
                                value={field.value ?? ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Meal description field */}
                    <FormField
                      control={form.control}
                      name={section.mealName as keyof dietrydata}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Enter meal details"
                              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-[#42679e] font-medium"
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button type="submit" disabled={ isPending || form.formState.isSubmitting}>
                { isPending ? (
                  <span className="loading loading-dots loading-md"></span>
                ) : (
                  "Submit Dietary Parameters"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default DietaryReportForm